/**
 * Трансформеры для преобразования данных между API и локальным форматом
 */

import type { QueuedCallApiResponse, AddToQueueApiRequest, UpdateQueuedCallApiRequest } from '../types/call-queue-api.types';
import type { QueuedCall, AddToQueuePayload, UpdateQueuedCallPayload, QueuedCallPriority, QueuedCallStatus } from '../types/call-queue.types';
import type { TelephonyCallStage, TelephonyDirection, TelephonySubjectType } from '../types/telephony.events';
import { DEFAULT_URGENT_THRESHOLD_MS, DEFAULT_OVERDUE_THRESHOLD_MS } from '../types/call-queue.types';

/**
 * Преобразование API ответа в локальный формат
 */
export function transformApiToLocal(apiCall: QueuedCallApiResponse): QueuedCall {
  const serverCreatedAt = new Date(apiCall.created_at).getTime();
  const serverUpdatedAt = new Date(apiCall.updated_at).getTime();

  return {
    // Серверные поля
    id: apiCall.id,
    odUserId: apiCall.user_id,
    odUserFullName: apiCall.user_full_name,
    serverCreatedAt: apiCall.created_at,
    serverUpdatedAt: apiCall.updated_at,

    // Основные поля
    callId: apiCall.call_id,
    phone: apiCall.phone,
    direction: apiCall.direction as TelephonyDirection,
    subjectType: apiCall.subject_type as TelephonySubjectType | undefined,
    subjectId: apiCall.subject_id,
    stageCode: apiCall.stage_code,
    customerId: apiCall.customer_id,
    customerFullName: apiCall.customer_full_name,
    customerIin: apiCall.customer_iin,
    telephonyEvent: apiCall.telephony_event as TelephonyCallStage | undefined,
    recordingUrl: apiCall.recording_url,
    recordingReady: apiCall.recording_ready,
    resultCode: apiCall.result_code,
    comment: apiCall.comment,

    // Метаданные для UI
    createdAt: serverCreatedAt,
    lastActivityAt: serverUpdatedAt,

    // Статус и приоритет
    status: (apiCall.status as QueuedCallStatus) || 'active',
    priority: (apiCall.priority as QueuedCallPriority) || calculatePriority(serverCreatedAt),
    seen: apiCall.seen,
  };
}

/**
 * Преобразование локального payload в API запрос
 */
export function transformLocalToApiRequest(payload: AddToQueuePayload): AddToQueueApiRequest {
  return {
    call_id: payload.callId,
    phone: payload.phone,
    direction: payload.direction || 'inbound',
    subject_type: payload.subjectType,
    subject_id: payload.subjectId != null ? String(payload.subjectId) : undefined,
    stage_code: payload.stageCode,
    customer_id: payload.customerId,
    customer_full_name: payload.customerFullName,
    customer_iin: payload.customerIin,
    telephony_event: payload.telephonyEvent,
  };
}

/**
 * Преобразование payload обновления в API запрос
 */
export function transformUpdateToApiRequest(payload: UpdateQueuedCallPayload): UpdateQueuedCallApiRequest {
  return {
    status: payload.status,
    telephony_event: payload.telephonyEvent,
    recording_url: payload.recordingUrl,
    recording_ready: payload.recordingReady,
    seen: payload.seen,
    customer_id: payload.customerId,
    customer_full_name: payload.customerFullName,
  };
}

/**
 * Вычисление приоритета на основе времени
 */
export function calculatePriority(
  createdAt: number,
  urgentThreshold: number = DEFAULT_URGENT_THRESHOLD_MS,
  overdueThreshold: number = DEFAULT_OVERDUE_THRESHOLD_MS
): QueuedCallPriority {
  const elapsed = Date.now() - createdAt;
  if (elapsed >= overdueThreshold) {
    return 'overdue';
  }
  if (elapsed >= urgentThreshold) {
    return 'urgent';
  }
  return 'normal';
}

/**
 * Создание временной карточки для optimistic update
 */
export function createTempCall(payload: AddToQueuePayload): QueuedCall {
  const now = Date.now();
  return {
    // Временные серверные поля (будут заменены после ответа API)
    id: -1,
    odUserId: -1,
    odUserFullName: '',
    serverCreatedAt: new Date(now).toISOString(),
    serverUpdatedAt: new Date(now).toISOString(),

    // Основные поля
    callId: payload.callId,
    phone: payload.phone,
    direction: payload.direction,
    subjectType: payload.subjectType,
    subjectId: payload.subjectId,
    stageCode: payload.stageCode,
    customerId: payload.customerId,
    customerFullName: payload.customerFullName,
    customerIin: payload.customerIin,
    telephonyEvent: payload.telephonyEvent,

    // Метаданные для UI
    createdAt: now,
    lastActivityAt: now,

    // Статус и приоритет
    status: 'active',
    priority: 'normal',
    seen: false,
  };
}

/**
 * Мерж серверных данных с локальными (server wins для конфликтов)
 */
export function mergeServerWithLocal(serverCall: QueuedCall, localCall: QueuedCall): QueuedCall {
  // Серверные данные имеют приоритет, но сохраняем локальные UI-данные если они новее
  const serverTime = serverCall.serverUpdatedAt
    ? new Date(serverCall.serverUpdatedAt).getTime()
    : serverCall.lastActivityAt;
  const localTime = localCall.lastActivityAt;

  if (serverTime >= localTime) {
    return serverCall;
  }

  // Локальные изменения новее - мержим
  return {
    ...serverCall,
    // Сохраняем локальные UI-поля
    seen: localCall.seen || serverCall.seen,
    priority: calculatePriority(serverCall.createdAt),
  };
}

/**
 * Проверка, является ли карточка устаревшей
 */
export function isCallExpired(call: QueuedCall, maxAgeMs: number): boolean {
  return Date.now() - call.createdAt > maxAgeMs;
}

/**
 * Сортировка очереди по приоритету и времени
 */
export function sortQueueByPriority(queue: QueuedCall[]): QueuedCall[] {
  const priorityOrder: Record<QueuedCallPriority, number> = {
    overdue: 0,
    urgent: 1,
    normal: 2,
  };

  return [...queue].sort((a, b) => {
    // Сначала по приоритету
    const priorityDiff = priorityOrder[a.priority] - priorityOrder[b.priority];
    if (priorityDiff !== 0) return priorityDiff;
    // Затем по времени (старые первые)
    return a.createdAt - b.createdAt;
  });
}
